/**
 * SetArubaKeyPage — Aruba key setup + management.
 *
 * When the user has no key, this is the first-time setup page. Once they have
 * at least one key, this becomes a Manage Keys page with create/rename/delete
 * and activate actions. Secret credentials are never editable post-create.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight,
  KeyRound,
  AlertTriangle,
  ShieldCheck,
  Eye,
  EyeOff,
  Pencil,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Carousel, type CarouselSlide } from "@/components/ui/carousel";
import { PanelSkeleton } from "@/components/ui/skeleton";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { useArubaKeys } from "@/stores/aruba-keys";
import {
  ArubaKeyMutationSchema,
  type ArubaKeyMutation,
} from "@/api/schemas/aruba";
import { ApiError } from "@/api/client";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const ARUBA_KEY_SLIDES: CarouselSlide[] = [
  {
    id: "step-1",
    imageSrc: "/static/app_aruba/img/add-aruba-key-step1.png",
    imageAlt:
      "Aruba Central classic dashboard navigation showing Organization and Platform Integration",
    title: "Step 1 - Open API Gateway",
    body: (
      <>
        Go to Aruba Central classic dashboard. Open Organization, then Platform
        Integration, then REST API under API Gateway.
      </>
    ),
  },
  {
    id: "step-2",
    imageSrc: "/static/app_aruba/img/add-aruba-key-step2.png",
    imageAlt: "My Apps and Tokens tab in Aruba Central REST API page",
    title: "Step 2 - Open My Apps and Tokens",
    body: <>In REST API, open the My Apps and Tokens tab.</>,
  },
  {
    id: "step-3",
    imageSrc: "/static/app_aruba/img/add-aruba-key-step3.png",
    imageAlt: "Generate token dialog in Aruba Central",
    title: "Step 3 - Generate a token",
    body: (
      <>
        Click Add Apps and Tokens, keep defaults, then click Generate to create
        a new token bundle.
      </>
    ),
  },
  {
    id: "step-4",
    imageSrc: "/static/app_aruba/img/add-aruba-key-step4.png",
    imageAlt: "Client ID and Client Secret table in Aruba Central",
    title: "Step 4 - Copy client credentials",
    body: (
      <>
        Copy client_id and client_secret from the My Apps and Tokens table.
        Expand columns if values are truncated.
      </>
    ),
  },
  {
    id: "step-5",
    imageSrc: "/static/app_aruba/img/add-aruba-key-step5.png",
    imageAlt: "Downloaded token JSON showing access_token and refresh_token",
    title: "Step 5 - Copy access and refresh tokens",
    body: (
      <>
        Use Download Token and copy access_token and refresh_token from the JSON
        response.
      </>
    ),
  },
];

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }

  const data = err.data;
  if (data && typeof data === "object") {
    const maybeDetail = (data as { detail?: unknown }).detail;
    if (typeof maybeDetail === "string" && maybeDetail.trim().length > 0) {
      return maybeDetail;
    }
    const maybeMessage = (data as { message?: unknown }).message;
    if (typeof maybeMessage === "string" && maybeMessage.trim().length > 0) {
      return maybeMessage;
    }
  }

  return `API error ${err.status}`;
}

export function SetArubaKeyPage() {
  const animatedRootRef = useUiAutoAnimate();
  const { data: keys, status, error, load } = useArubaKeys();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  useEffect(() => {
    void load();
  }, [load]);

  const hasKeys = keys.length > 0;
  const activeKey = useMemo(
    () => keys.find((k) => k.active && !k.stale),
    [keys],
  );
  const stale = keys.filter((k) => k.stale);

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">
          {hasKeys ? "Manage keys" : "Set API key"}
        </span>
      </div>

      <ArubaSubNav />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            {hasKeys ? "Manage Aruba keys" : "Set Aruba API key"}
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            {hasKeys
              ? "Create additional keys, rename labels, activate another key, or remove keys you no longer use. Secret credentials remain immutable after creation."
              : "Opus needs a Greenlake / Aruba Central OAuth key to enumerate your devices and push configuration. All secret values are stored encrypted at rest."}
          </p>
        </div>
        <Badge variant="info" size="md">
          <ShieldCheck size={11} />
          Stored encrypted at rest
        </Badge>
      </div>

      {error ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {activeKey && redirect ? (
        <div className="flex justify-end">
          <Button asChild>
            <Link to={redirect}>Continue</Link>
          </Button>
        </div>
      ) : null}

      {!hasKeys ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span>
            No Aruba keys are registered yet. Add your first key to unlock the
            Aruba pages.
          </span>
        </div>
      ) : null}

      {hasKeys && stale.length > 0 ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">{stale.length}</b> stored key
            {stale.length === 1 ? "" : "s"} are stale — their refresh tokens
            expired. Re-paste fresh tokens below.
          </span>
        </output>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(280px,0.7fr)]">
        <ArubaKeyFormCard />
        <ArubaKeysListCard />
      </div>

      {status === "loading" && keys.length === 0 ? <PanelSkeleton /> : null}
    </div>
  );
}

function ArubaKeyFormCard() {
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showRefreshToken, setShowRefreshToken] = useState(false);
  const { data: keys, create } = useArubaKeys();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const hasKeys = keys.length > 0;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ArubaKeyMutation>({
    resolver: zodResolver(ArubaKeyMutationSchema),
    defaultValues: {
      name: "",
      clientId: "",
      clientSecret: "",
      accessToken: "",
      refreshToken: "",
      customerId: "",
      region: "apigw-ca.central.arubanetworks.com",
      active: true,
    },
  });

  async function onCreate(values: ArubaKeyMutation) {
    try {
      await create(values);
      toast.success("Aruba key saved");
      if (redirect) navigate(redirect);
    } catch (err) {
      toast.error("Unable to create key", {
        description: getApiErrorMessage(
          err,
          "Credential validation failed. Please review your Aruba credentials.",
        ),
      });
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardContent>
          <form
            noValidate
            onSubmit={(e) => void handleSubmit(onCreate)(e)}
            className="grid grid-cols-1 gap-4 xl:grid-cols-2"
          >
            <div className="xl:col-span-2">
              <CardTitle>
                {hasKeys ? "Add a new Aruba key" : "Add your first Aruba key"}
              </CardTitle>
              <CardSub>
                New keys are validated against Aruba before being stored.
              </CardSub>
            </div>
            <div className="xl:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor="name">Label</Label>
              <Input
                id="name"
                placeholder="e.g. GuestTek · Greenlake prod"
                invalid={!!errors.name}
                {...register("name")}
              />
              {errors.name ? (
                <span className="text-xs text-rose">{errors.name.message}</span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cid">client_id</Label>
              <div className="relative">
                <KeyRound
                  size={14}
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
                />
                <Input
                  id="cid"
                  className="pl-9 font-mono text-xs"
                  autoComplete="off"
                  spellCheck={false}
                  invalid={!!errors.clientId}
                  {...register("clientId")}
                />
              </div>
              {errors.clientId ? (
                <span className="text-xs text-rose">
                  {errors.clientId.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="csec">client_secret</Label>
              <div className="relative">
                <Input
                  id="csec"
                  type={showClientSecret ? "text" : "password"}
                  className="pr-16 font-mono text-xs"
                  autoComplete="off"
                  spellCheck={false}
                  invalid={!!errors.clientSecret}
                  {...register("clientSecret")}
                />
                <button
                  type="button"
                  aria-label={showClientSecret ? "Hide client secret" : "Show client secret"}
                  aria-pressed={showClientSecret}
                  onClick={() => setShowClientSecret((v) => !v)}
                  className="absolute right-2 top-1/2 inline-flex h-7 -translate-y-1/2 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                >
                  {showClientSecret ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showClientSecret ? "Hide" : "Show"}
                </button>
              </div>
              {errors.clientSecret ? (
                <span className="text-xs text-rose">
                  {errors.clientSecret.message}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="atok">access_token</Label>
              <div className="relative">
                <Input
                  id="atok"
                  type={showAccessToken ? "text" : "password"}
                  className="pr-16 font-mono text-xs"
                  autoComplete="off"
                  spellCheck={false}
                  invalid={!!errors.accessToken}
                  {...register("accessToken")}
                />
                <button
                  type="button"
                  aria-label={showAccessToken ? "Hide access token" : "Show access token"}
                  aria-pressed={showAccessToken}
                  onClick={() => setShowAccessToken((v) => !v)}
                  className="absolute right-2 top-1/2 inline-flex h-7 -translate-y-1/2 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                >
                  {showAccessToken ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showAccessToken ? "Hide" : "Show"}
                </button>
              </div>
              {errors.accessToken ? (
                <span className="text-xs text-rose">
                  {errors.accessToken.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rtok">refresh_token</Label>
              <div className="relative">
                <Input
                  id="rtok"
                  type={showRefreshToken ? "text" : "password"}
                  className="pr-16 font-mono text-xs"
                  autoComplete="off"
                  spellCheck={false}
                  invalid={!!errors.refreshToken}
                  {...register("refreshToken")}
                />
                <button
                  type="button"
                  aria-label={showRefreshToken ? "Hide refresh token" : "Show refresh token"}
                  aria-pressed={showRefreshToken}
                  onClick={() => setShowRefreshToken((v) => !v)}
                  className="absolute right-2 top-1/2 inline-flex h-7 -translate-y-1/2 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                >
                  {showRefreshToken ? <EyeOff size={12} /> : <Eye size={12} />}
                  {showRefreshToken ? "Hide" : "Show"}
                </button>
              </div>
              {errors.refreshToken ? (
                <span className="text-xs text-rose">
                  {errors.refreshToken.message}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="region">API gateway</Label>
              <Input
                id="region"
                placeholder="apigw-ca.central.arubanetworks.com"
                className="font-mono text-xs"
                autoComplete="off"
                spellCheck={false}
                invalid={!!errors.region}
                {...register("region")}
              />
              {errors.region ? (
                <span className="text-xs text-rose">
                  {errors.region.message}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cust">customer_id (optional)</Label>
              <Input
                id="cust"
                placeholder="cu-xxxx"
                className="font-mono text-xs"
                {...register("customerId")}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="xl:col-span-2 mt-2"
            >
              {isSubmitting ? "Validating credentials…" : "Create key"}
              <ArrowRight size={14} />
            </Button>
          </form>
        </CardContent>
      </Card>

      <Carousel slides={ARUBA_KEY_SLIDES} className="max-w-none" />
    </div>
  );
}

function ArubaKeysListCard() {
  const { data: keys, activate, remove, rename } = useArubaKeys();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState("");

  async function onActivate(id: number) {
    try {
      await activate(id);
      toast.success("Aruba key activated");
    } catch (err) {
      toast.error("Unable to activate key", {
        description:
          err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown",
      });
    }
  }

  async function onRemove(id: number, name: string) {
    if (!window.confirm(`Remove Aruba key "${name}"?`)) return;
    try {
      await remove(id);
      toast.success(`Removed Aruba key "${name}"`);
      if (editingId === id) {
        setEditingId(null);
        setDraftName("");
      }
    } catch (err) {
      toast.error("Unable to remove key", {
        description:
          err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown",
      });
    }
  }

  function beginRename(id: number, name: string) {
    setEditingId(id);
    setDraftName(name);
  }

  async function saveRename(id: number) {
    const next = draftName.trim();
    if (!next) {
      toast.error("Name is required");
      return;
    }
    try {
      await rename(id, next);
      toast.success("Key name updated");
      setEditingId(null);
      setDraftName("");
    } catch (err) {
      toast.error("Unable to rename key", {
        description:
          err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown",
      });
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div>
          <CardTitle>Registered keys</CardTitle>
          <CardSub>
            You can rename labels, activate another key, or remove keys.
            Secret credentials cannot be edited.
          </CardSub>
        </div>

        {keys.length === 0 ? (
          <div className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg-dim">
            No keys registered yet.
          </div>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {keys.map((k) => {
              const isEditing = editingId === k.id;
              return (
                <li
                  key={k.id}
                  className="flex items-start gap-3 rounded-lg border border-line bg-surface p-3"
                >
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <Input
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        className="h-8"
                        autoFocus
                      />
                    ) : (
                      <div className="truncate text-sm font-semibold text-fg">
                        {k.name}
                      </div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {k.active ? (
                        <Badge variant="ok" size="sm">active</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">inactive</Badge>
                      )}
                      {k.stale ? (
                        <Badge variant="warn" size="sm">stale</Badge>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void saveRename(k.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                        >
                          <Save size={10} />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditingId(null); setDraftName(""); }}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                        >
                          <X size={10} />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => beginRename(k.id, k.name)}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                        >
                          <Pencil size={10} />
                          Rename
                        </button>
                        {!k.active ? (
                          <button
                            type="button"
                            onClick={() => void onActivate(k.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg"
                          >
                            Activate
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => void onRemove(k.id, k.name)}
                          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-semibold text-fg-mute hover:border-rose/40 hover:bg-rose/10 hover:text-rose"
                        >
                          <Trash2 size={10} />
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
